import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../tailwind-chat/ui/dialog";
import { ScrollArea } from "../tailwind-chat/ui/scroll-area";
import { updateChatInfo } from "@/app/(chat)/actions";
import type { ChatInfo, Chat } from "@/lib/db/schema";

// export default function ChatEditorDialog({ 
//   showInfoDialog, 
//   setShowInfoDialog, 
//   updateChat, shareChatMsg,
//   onSaveSuccess 
// }:{
//   showInfoDialog: boolean, 
//   setShowInfoDialog: (show:boolean) => void, 
//   updateChat:any, 
//   shareChatMsg:any,
//   onSaveSuccess: (data:any) => void; 
// }) {

interface ChatEditorDialogProps {
  showInfoDialog: boolean;
  setShowInfoDialog: (open: boolean) => void;
  updateChat: Chat | null; // 可能是 null，如果还没加载或选中
  shareChatMsg: any;
  onSaveSuccess?: (newInfo: ChatInfo) => void;
}

export default function ChatEditorDialog({ 
  showInfoDialog, 
  setShowInfoDialog, 
  updateChat, 
  shareChatMsg,
  onSaveSuccess 
}: ChatEditorDialogProps) {
  const [jsonString, setJsonString] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  

  const MAX_HEIGHT = 1200;
    
  useEffect(() => {
    requestAnimationFrame(adjustTextareaHeight);
  }, [jsonString, showInfoDialog]);

  // 1. 创建 ref 来直接操作 DOM 元素
  const textareaRef = useRef<HTMLTextAreaElement>(null);


  useEffect(() => {
    if (updateChat) {
      setJsonString(JSON.stringify(updateChat?.info, null, 2));
      setError("");
    }
  }, [updateChat, showInfoDialog]);

  // 2. 核心逻辑：自动调整高度函数
  const adjustTextareaHeight = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      // 先重置高度，防止删除文字后高度不回缩
      textarea.style.height = "auto";
      // 设置为滚动高度（即内容的真实高度）
      // textarea.style.height = `${textarea.scrollHeight}px`;

      textarea.style.height =
        `${Math.min(textarea.scrollHeight, MAX_HEIGHT)}px`;
    }
  };

  // 3. 当内容(jsonString)变化或弹窗打开时，重新计算高度
  useEffect(() => {
    adjustTextareaHeight();
  }, [jsonString, showInfoDialog]);

const handleSave = async () => {
    if (!updateChat?.id) {
      toast.error("无法获取对话 ID");
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      // 1. 校验 JSON 格式
      let parsedInfo: ChatInfo;
      try {
        parsedInfo = JSON.parse(jsonString);
      } catch (e) {
        // biome-ignore lint/style/useTemplate: <explanation>
        throw new Error("JSON 格式错误，请检查语法:" + String(e));
      }
      
      // 2. 调用后端 API
      const response = await fetch('/api/chat/3022', {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: updateChat.id,
          info: parsedInfo
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || "更新失败");
      }

      // 3. 处理成功
      const updatedData = await response.json();
      console.log("更新成功:", updatedData);
      
      toast.success("Chat Info 更新成功！");
      setShowInfoDialog(false);
      
      // 通知父组件更新状态
      if (onSaveSuccess) {
        onSaveSuccess(parsedInfo);
      }

    } catch (err: any) {
      console.error(err);
      setError(err?.message || "未知错误");
      toast.error(err?.message || "保存失败");
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e:any) => {
    e.stopPropagation();
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    }
  };

  return (
    <Dialog open={showInfoDialog} onOpenChange={setShowInfoDialog}>
      <DialogContent className="flex flex-col max-w-3xl max-h-[100hv] h-screen">
        <DialogHeader>
          <DialogTitle>编辑对话信息: {updateChat?.title}</DialogTitle>
          <DialogDescription>
            请直接编辑下方的chat.info JSON 数据并保存。
          </DialogDescription>
        </DialogHeader>

        {/* 
           ScrollArea 限制高度为 60vh。
           当内部的 Textarea 变高超过 60vh 时，这里会出现滚动条。
        */}
        <ScrollArea className="h-[700px] w-full rounded-md border bg-muted p-4">
            <div className="flex flex-col gap-4">
              {/* 预览区域 */}
              <div className="w-full overflow-hidden rounded-md bg-slate-950 p-4 text-xs text-white shrink-0">
                <pre className="overflow-auto max-h-[150px]">
                  <code>{JSON.stringify(updateChat, null, 2)}</code>
                </pre>
              </div>
              <div className="w-full overflow-hidden rounded-md bg-slate-950 p-4 text-xs text-white shrink-0">
                <p>{"当前选中的 shareChats:"}</p>
                <pre className="overflow-auto max-h-[150px]">
                  <code> {JSON.stringify(shareChatMsg, null, 2)}</code>
                </pre>
              </div>
              

              {/* 编辑区域 */}
              {/* 移除了 min-h-[800px] 的强制包裹，让 Textarea 自己决定高度 */}
              <div className="flex flex-col gap-2 pb-4">
                <Textarea 
                  ref={textareaRef} // 绑定 ref
                  value={jsonString}
                  onChange={(e) => {
                    setJsonString(e.target.value);
                    // onChange 时也会触发 useEffect，但直接调用更流畅
                    adjustTextareaHeight(); 
                  }}
                  onKeyDown={handleKeyDown}
                  // 关键样式：
                  // 1. min-h-[300px]: 保证初始数据少时也有一定高度，方便点击
                  // 2. overflow-hidden: 隐藏 Textarea 自身的滚动条，完全依赖外部 ScrollArea
                  // 3. resize-none: 禁止用户手动拖拽，全靠自动计算
                  // className="w-full min-h-[600px] font-mono text-sm bg-slate-50 dark:bg-slate-950 resize-none overflow-hidden"
                      className="
                        w-full
                        min-h-[300px]
                        resize-none
                        overflow-hidden
                        font-mono
                        text-sm
                        bg-slate-50 dark:bg-slate-950
                      "
                  spellCheck={false}
                  placeholder="在此输入 JSON..."
                />
                
                {error && (
                  <div className="text-red-500 text-sm font-medium bg-red-50 p-2 rounded border border-red-200">
                    ❌ {error}
                  </div>
                )}
              </div>
            </div>
        </ScrollArea>

        <DialogFooter>
          <Button variant="outline" onClick={() => setShowInfoDialog(false)} disabled={isLoading}>
            取消
          </Button>
          <Button onClick={handleSave} disabled={isLoading}>
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                保存中...
              </>
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" />
                保存更改
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
